'use client'

import { Backdrop, Button, ButtonVariants, Text } from '../../atoms'
import { CloseIcon } from '../../icons'
import { ModalSizes } from './types/modalTypes'
import { DictionaryContext } from '../../../providers'
import { TextTypes } from '../../../types'
import React, { RefObject, useContext } from 'react'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../constants/iconButtonAriaLabelMessageKeys'

export interface ModalContentProps {
    title?: string | React.ReactNode
    size?: ModalSizes
    children?: React.ReactNode
    footer?: React.ReactNode
    headerComponent?: React.ReactNode
    canBeClosed?: boolean
    hasBackdrop?: boolean
    stackedButtons?: boolean
    fullContentWidth?: boolean
    className?: string
    modalLayerRef: RefObject<HTMLDivElement | null>
    dialogRef: RefObject<HTMLDivElement | null>
    onClose: () => void
    onBackdropClick: () => void
}

export const ModalContent = ({
    children,
    title,
    size = ModalSizes.MD,
    stackedButtons,
    footer,
    headerComponent,
    canBeClosed = true,
    hasBackdrop = true,
    fullContentWidth = false,
    className = '',
    modalLayerRef,
    dialogRef,
    onClose,
    onBackdropClick,
    ...props
}: ModalContentProps) => {
    const dictionary = useContext(DictionaryContext)

    return (
        <div
            ref={modalLayerRef}
            {...props}
            className="modal fixed top-0 left-0 right-0 bottom-0 z-50 p-6 inline-flex justify-content items-center max-w"
        >
            {hasBackdrop && (
                <div className="modal__backdrop-wrapper" onMouseDown={onBackdropClick}>
                    <Backdrop className="modal__backdrop" />
                </div>
            )}
            <div
                ref={dialogRef}
                className={`modal__dialog bg-shade-100 shadow-drop-lg w-full ${size} m-auto rounded max-h-full flex ${className}`}
            >
                <div className={`modal__content max-h-full flex flex-col w-full ${size}`}>
                    <div
                        className={`modal__header flex items-center w-full pt-4 pl-6 pr-4 pb-4 max-w ${
                            headerComponent ? 'border-b border-b-shade-200 mb-4' : ''
                        }`}
                    >
                        {title && (
                            <Text className="mr-4" type={TextTypes.HEADING_3}>
                                {title}
                            </Text>
                        )}
                        {headerComponent && headerComponent}
                        {canBeClosed && (
                            <Button
                                onClick={onClose}
                                variant={ButtonVariants.QUIET}
                                className="ml-auto"
                                icon={<CloseIcon />}
                                data-testid="close-modal-button"
                                aria-label={resolveIconButtonAriaLabel(
                                    dictionary,
                                    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.CLOSE,
                                )}
                            />
                        )}
                    </div>
                    {children && (
                        <div
                            className={`modal__body ${
                                fullContentWidth ? '' : 'px-6'
                            } pt-2 pb-4 text-text-default overflow-y-auto overflow-x-hidden`}
                        >
                            {children}
                        </div>
                    )}
                    {footer && (
                        <div
                            className={`modal__footer flex ${
                                stackedButtons ? 'flex-col gap-2' : 'gap-4'
                            } justify-end items-center pt-2 pb-6 px-6`}
                        >
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
